import { useCallback, useEffect, useRef, useState } from 'react';
import Modal from './Modal';

// U-ADM-06 · pre-launch audit hardening.
//
// window.confirm / window.alert / window.prompt render unstyled in
// RTL, break the visual language, can be blocked by browser settings,
// and don't fit the app's tone. Twelve+ call sites use them for
// destructive + data-entry actions.
//
// `useConfirm()` returns an async `confirm(options)` that opens a
// styled Modal with a title, body, cancel + confirm buttons. Awaits
// the user's choice as a boolean. Use it in place of window.confirm
// for anything with visible consequences (delete, revoke, close a
// thread, save an HR decision, etc.).
//
// Also exports `AsyncConfirmSlot` — a component to mount once at the
// app root; it renders the Modal wherever the hook has been called.

interface ConfirmOptions {
  title?: string;
  body?: string;
  confirmText?: string;
  cancelText?: string;
  // 'danger' renders the confirm button in red so admins spot a
  // destructive action.
  tone?: 'default' | 'danger';
}

type Pending = ConfirmOptions & { resolve: (v: boolean) => void };

// Module-level state so any component can call the hook without
// prop-drilling. The <AsyncConfirmSlot> below reads from this.
let notify: () => void = () => {};
let current: Pending | null = null;

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  return useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      current = { ...opts, resolve };
      notify();
    });
  }, []);
}

export function AsyncConfirmSlot() {
  const [, tick] = useState(0);
  const nRef = useRef(0);

  useEffect(() => {
    notify = () => {
      nRef.current += 1;
      tick(nRef.current);
    };
    return () => {
      notify = () => {};
    };
  }, []);

  if (!current) return null;
  const c = current;
  const settle = (v: boolean) => {
    current = null;
    c.resolve(v);
    notify();
  };

  const tone = c.tone ?? 'default';

  return (
    <Modal
      open
      onClose={() => settle(false)}
      title={c.title ?? 'تأكيد'}
      actions={
        <>
          <button type="button" className="btn btn-secondary" onClick={() => settle(false)}>
            {c.cancelText ?? 'إلغاء'}
          </button>
          <button
            type="button"
            className={tone === 'danger' ? 'btn btn-danger' : 'btn'}
            onClick={() => settle(true)}
            autoFocus
          >
            {c.confirmText ?? 'تأكيد'}
          </button>
        </>
      }
    >
      {c.body && (
        <p style={{ color: 'var(--text-secondary)', marginTop: 0, whiteSpace: 'pre-line' }}>
          {c.body}
        </p>
      )}
    </Modal>
  );
}
